import React, { useState, useEffect } from 'react';
import Helmet from '../components/Helmet/Helmet';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/login.css';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase.config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { getAuthErrorMessage } from '../services/authErrors';
import useAuth from '../custom-hooks/useAuth';
import bannerImg from '../assets/images/banner-1.jpg';

import { useDispatch } from 'react-redux';
import { cartActions } from '../redux/slices/cartSlice';
import { wishlistActions } from '../redux/slices/wishlistSlice';
import { fetchUserData } from '../services/userSyncService';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
    />
  </svg>
);

const LogIn = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState(
    location.state?.alreadyRegistered
      ? 'You already have an account! Please enter your password to sign in.'
      : ''
  );

  // If already logged in, redirect to appropriate destination
  useEffect(() => {
    if (!authLoading && currentUser && !loading && !googleLoading) {
      if (currentUser.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    }
  }, [currentUser, authLoading, loading, googleLoading, navigate]);

  useEffect(() => {
    if (location.state?.alreadyRegistered) {
      setInfoMessage('You already have an account! Please enter your password to sign in.');
      toast.info('You are already registered! Please enter your password to sign in.');
    }
  }, [location.state]);

  const signin = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email.trim() || !password) {
      const msg = 'Please enter both your email and password.';
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    setLoading(true);

    try {
      // Configure Firebase auth session persistence based on user preference
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const user = userCredential.user;

      // 1. Fetch complete profile from Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userDocRef);
      const userData = userSnap.exists() ? userSnap.data() : null;

      // Admin access is decided only by the role stored in users/{uid}.
      // The role can only be granted by an existing admin (see firestore.rules).
      const isAdminAccount = userData?.role === 'admin';

      const profileData = {
        uid: user.uid,
        email: user.email,
        displayName: userData?.displayName || user.displayName || user.email?.split('@')[0],
        photoURL: userData?.photoURL || user.photoURL || null,
        role: userData?.role || 'customer',
      };

      // 2. Cache immediately in localStorage so useAuth and Header have zero flash/delay
      localStorage.setItem('cachedAuthProfile', JSON.stringify(profileData));

      // 3. Hydrate Redux cart & wishlist from Firestore BEFORE redirecting
      const { cart, wishlist } = await fetchUserData(user.uid);
      dispatch(cartActions.setCart(cart));
      dispatch(wishlistActions.setWishlist(wishlist));

      setLoading(false);

      if (isAdminAccount) {
        toast.success(`Welcome to the Atelier Command Center, ${profileData.displayName}!`, {
          icon: '👑',
          autoClose: 3500,
        });
        navigate('/admin');
      } else {
        const redirectTarget = location.state?.from || '/';
        toast.success(`Welcome back, ${profileData.displayName}!`);
        navigate(redirectTarget);
      }
    } catch (error) {
      setLoading(false);
      const friendlyError = getAuthErrorMessage(error);
      setErrorMessage(friendlyError);
      toast.error(friendlyError);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorMessage('');
    setGoogleLoading(true);

    try {
      // Set persistence for Google Sign-in as well
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Ensure user document exists in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userDocRef);
      let userData = userSnap.exists() ? userSnap.data() : null;

      if (!userSnap.exists()) {
        userData = {
          uid: user.uid,
          displayName: user.displayName || user.email.split('@')[0],
          email: user.email,
          photoURL: user.photoURL || null,
          createdAt: new Date().toISOString(),
          role: 'customer',
        };
        await setDoc(userDocRef, userData);
      } else if (user.photoURL && userData?.photoURL !== user.photoURL) {
        await setDoc(userDocRef, { photoURL: user.photoURL }, { merge: true });
        userData = { ...userData, photoURL: user.photoURL };
      }

      // Cache user profile in localStorage
      const profileData = {
        uid: user.uid,
        email: user.email,
        displayName: userData?.displayName || user.displayName || user.email?.split('@')[0],
        photoURL: user.photoURL || userData?.photoURL || null,
        role: userData?.role || 'customer',
      };
      localStorage.setItem('cachedAuthProfile', JSON.stringify(profileData));

      // Hydrate Redux cart & wishlist before redirecting
      const { cart, wishlist } = await fetchUserData(user.uid);
      dispatch(cartActions.setCart(cart));
      dispatch(wishlistActions.setWishlist(wishlist));

      setGoogleLoading(false);
      const redirectTarget = location.state?.from || '/';
      toast.success(`Welcome back, ${profileData.displayName}!`);
      navigate(redirectTarget);
    } catch (error) {
      setGoogleLoading(false);
      if (error.code === 'auth/popup-closed-by-user') {
        return;
      }
      const friendlyError = getAuthErrorMessage(error);
      setErrorMessage(friendlyError);
      toast.error(friendlyError);
    }
  };


  return (
    <Helmet title="Sign In | L'HABILLEUR">
      <section className="auth__section">
        <div className="auth__container-wide">
          <motion.div
            className="auth__card-split"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* Side Showcase Banner */}
            <div 
              className="auth__banner-side"
              style={{ backgroundImage: `url(${bannerImg})` }}
            >
              <div className="auth__banner-overlay"></div>
              <div className="auth__banner-content">
                <div className="auth__banner-brand">
                  <h2>L'HABILLEUR</h2>
                  <p>Experience the art of living with premium furniture & handcrafted lifestyle collections.</p>
                </div>

                <div className="auth__banner-features">
                  <div className="auth__feature-item">
                    <i className="ri-shield-star-line"></i>
                    <span>Official Warranty on All Items</span>
                  </div>
                  <div className="auth__feature-item">
                    <i className="ri-customer-service-2-line"></i>
                    <span>24/7 Dedicated Support</span>
                  </div>
                  <div className="auth__feature-item">
                    <i className="ri-secure-payment-line"></i>
                    <span>Secure & Encrypted Checkout</span>
                  </div>
                </div>

                <div className="auth__banner-footer">
                  <i className="ri-shield-check-fill"></i>
                  <span>Encrypted 256-bit SSL connection</span>
                </div>
              </div>
            </div>

            {/* Right Column: Interactive Sign In Form */}
            <div className="auth__form-side">
              <div className="auth__header-compact">
                <h2>Welcome Back</h2>
                <p>Sign in to manage your orders, cart & wishlist</p>
              </div>

              {errorMessage && (
                <div className="auth__alert">
                  <i className="ri-error-warning-fill"></i>
                  <div>{errorMessage}</div>
                </div>
              )}

              {infoMessage && (
                <div className="auth__alert" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' }}>
                  <i className="ri-information-fill"></i>
                  <div>{infoMessage}</div>
                </div>
              )}

              {/* Google Sign-in Button */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="auth__google-btn"
              >
                {googleLoading ? (
                  <>
                    <i className="ri-loader-4-line ri-spin"></i> Connecting to Google...
                  </>
                ) : (
                  <>
                    <GoogleIcon /> Continue with Google
                  </>
                )}
              </button>

              <div className="auth__divider">
                <span>or sign in with email</span>
              </div>

              <form onSubmit={signin} noValidate>
                {/* Email */}
                <div className="auth__form-group">
                  <label htmlFor="login-email">Email Address</label>
                  <div className="auth__input-wrapper">
                    <i className="ri-mail-line auth__input-icon"></i>
                    <input
                      id="login-email"
                      type="email"
                      className="auth__input"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="auth__form-group">
                  <label htmlFor="login-password">Password</label>
                  <div className="auth__input-wrapper">
                    <i className="ri-lock-line auth__input-icon"></i>
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      className="auth__input"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="auth__toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="Toggle password visibility"
                    >
                      <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                </div>

                {/* Options: Remember Me & Forgot Password */}
                <div className="auth__options-row">
                  <label className="auth__checkbox-label" htmlFor="remember-me-checkbox">
                    <input
                      id="remember-me-checkbox"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="auth__checkbox"
                    />
                    <span>Remember me</span>
                  </label>

                  <Link
                    to="/forgot-password"
                    state={{ email }}
                    className="auth__forgot-link"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <button type="submit" className="auth__submit-btn" disabled={loading || googleLoading}>
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line ri-spin"></i> Loading Account...
                    </>
                  ) : (
                    <>
                      Sign In <i className="ri-login-box-line"></i>
                    </>
                  )}
                </button>

                <div className="auth__footer-text">
                  Don't have an account? <Link to="/signup">Create an account</Link>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </Helmet>
  );
};

export default LogIn;
